/**
 * Phase 27.45 — Event Request creation form.
 *
 * A single dialog split into three visual sections:
 *   1. Event Information (name / date / venue / contact)
 *   2. Business Opportunity (projected sales by category)
 *   3. Cost Information (total cost + amount requested)
 *
 * Attachments are handled in the detail dialog after creation to keep
 * this modal focused on the "get it into the system" step.
 */
import { useEffect, useMemo, useState } from 'react';
import { formatPhoneInput } from '@/lib/utils';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, DollarSign, Hash } from 'lucide-react';
import { eventRequestsAPI } from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import AddressAutocomplete from '@/components/common/AddressAutocomplete';
import { flashAutofilled } from '@/lib/flashAutofilled';
import { TimeSelect } from '@/components/common/TimeSelect';

const CATEGORY_LABELS = {
  medicare: 'Medicare',
  aca: 'ACA',
  life: 'Life',
  annuities: 'Annuities',
  ancillary: 'Ancillary',
  private_insurance: 'Private Insurance',
  travel_insurance: 'Travel Insurance',
  other: 'Other',
};

const EVENT_TYPE_LABELS = {
  community_health_fair: 'Community Health Fair',
  seminar: 'Seminar',
  expo: 'Expo',
  chamber_event: 'Chamber Event',
  sponsored_dinner: 'Sponsored Dinner',
  workshop: 'Workshop',
  other: 'Other',
};

const EMPTY = {
  event_name: '',
  event_type: 'community_health_fair',
  description: '',
  event_date: '',
  start_time: '09:00',
  end_time: '15:00',
  event_timezone: 'America/New_York',
  venue: '',
  address: '',
  city: '',
  state: '',
  zip_code: '',
  estimated_attendance: '',
  contact_name: '',
  contact_phone: '',
  contact_email: '',
  projected_sales: [],
  total_event_cost: '',
  amount_requested: '',
};

export default function EventRequestFormDialog({
  open, onOpenChange, onSaved,
  // Phase 27.48 — edit mode. Pass an existing event request doc to
  // switch this dialog into "edit" and hit PATCH instead of POST.
  editing = null,
}) {
  const isEdit = !!editing?.request_id;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [enums, setEnums] = useState({ business_categories: [], event_types: [] });

  useEffect(() => {
    if (!open) return;
    eventRequestsAPI.enums().then((r) => setEnums(r.data)).catch(() => {});
    if (isEdit) {
      setForm({
        ...EMPTY,
        event_name: editing.event_name || '',
        event_type: editing.event_type || 'community_health_fair',
        description: editing.description || '',
        event_date: (editing.event_date || '').slice(0, 10),
        start_time: editing.start_time || '09:00',
        end_time: editing.end_time || '15:00',
        event_timezone: editing.event_timezone || 'America/New_York',
        venue: editing.venue || '',
        address: editing.address || '',
        city: editing.city || '',
        state: editing.state || '',
        zip_code: editing.zip_code || '',
        estimated_attendance: editing.estimated_attendance ?? '',
        contact_name: editing.contact_name || '',
        contact_phone: editing.contact_phone || '',
        contact_email: editing.contact_email || '',
        projected_sales: (editing.projected_sales || []).map((l) => ({
          category: l.category, amount: l.amount,
        })),
        total_event_cost: editing.total_event_cost ?? '',
        amount_requested: editing.amount_requested ?? '',
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, isEdit, editing]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const addSalesLine = () => setForm((p) => ({
    ...p,
    projected_sales: [...p.projected_sales, { category: 'medicare', amount: '' }],
  }));
  const updateSalesLine = (idx, patch) => setForm((p) => ({
    ...p,
    projected_sales: p.projected_sales.map((l, i) => (i === idx ? { ...l, ...patch } : l)),
  }));
  const removeSalesLine = (idx) => setForm((p) => ({
    ...p,
    projected_sales: p.projected_sales.filter((_, i) => i !== idx),
  }));

  const totalProjected = useMemo(() => (
    (form.projected_sales || []).reduce((sum, l) => sum + (Number(l.amount) || 0), 0)
  ), [form.projected_sales]);

  const canSubmit = form.event_name.trim() && form.event_date && form.start_time && form.end_time
    && form.total_event_cost !== '' && form.amount_requested !== '';

  const submit = async () => {
    if (!canSubmit) {
      toast.error('Please fill event name, date, times and cost fields.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        estimated_attendance: form.estimated_attendance !== ''
          ? Number(form.estimated_attendance) : null,
        total_event_cost: Number(form.total_event_cost),
        amount_requested: Number(form.amount_requested),
        projected_sales: (form.projected_sales || [])
          .filter((l) => l.category && l.amount !== '' && Number(l.amount) > 0)
          .map((l) => ({ category: l.category, amount: Number(l.amount) })),
      };
      // Drop empty optional fields so the backend uses its defaults.
      Object.keys(payload).forEach((k) => {
        if (payload[k] === '' || payload[k] === null || payload[k] === undefined) {
          delete payload[k];
        }
      });
      // Keep the two cost fields even when zero.
      payload.total_event_cost = Number(form.total_event_cost);
      payload.amount_requested = Number(form.amount_requested);
      payload.event_date = form.event_date;
      payload.start_time = form.start_time;
      payload.end_time = form.end_time;
      payload.event_name = form.event_name.trim();

      if (isEdit) {
        await eventRequestsAPI.update(editing.request_id, payload);
        toast.success('Event request updated');
      } else {
        await eventRequestsAPI.create(payload);
        toast.success('Event request submitted');
      }
      onSaved?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail
        : (isEdit ? 'Failed to update event request' : 'Failed to submit event request'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[92vh] overflow-y-auto"
        data-testid="event-request-form-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading">
            {isEdit ? 'Edit Event Request' : 'New Event Request'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-2">
          {/* ── Event Information ─────────────────────────────── */}
          <section>
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Event Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Label>Event name *</Label>
                <Input
                  value={form.event_name}
                  onChange={(e) => set('event_name', e.target.value)}
                  placeholder="Community Health Fair"
                  data-testid="er-event-name"
                />
              </div>
              <div>
                <Label>Event type</Label>
                <Select value={form.event_type} onValueChange={(v) => set('event_type', v)}>
                  <SelectTrigger data-testid="er-event-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(enums.event_types || []).map((t) => (
                      <SelectItem key={t} value={t}>{EVENT_TYPE_LABELS[t] || t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Estimated attendance</Label>
                <Input
                  type="number" min={0}
                  value={form.estimated_attendance}
                  onChange={(e) => set('estimated_attendance', e.target.value)}
                  data-testid="er-attendance"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Description</Label>
                <Textarea
                  rows={2}
                  value={form.description}
                  onChange={(e) => set('description', e.target.value)}
                  placeholder="Short description of what will happen at the event…"
                  data-testid="er-description"
                />
              </div>
              <div>
                <Label>Event date *</Label>
                <Input
                  type="date"
                  value={form.event_date}
                  onChange={(e) => set('event_date', e.target.value)}
                  data-testid="er-event-date"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Start *</Label>
                  <TimeSelect
                    value={form.start_time}
                    onChange={(v) => set('start_time', v)}
                    testId="er-start-time"
                  />
                </div>
                <div>
                  <Label>End *</Label>
                  <TimeSelect
                    value={form.end_time}
                    onChange={(v) => set('end_time', v)}
                    testId="er-end-time"
                  />
                </div>
              </div>
              <div className="md:col-span-2">
                <Label>Venue / Location</Label>
                <AddressAutocomplete
                  value={form.venue}
                  onChange={(v) => set('venue', v)}
                  onPlaceSelected={(parsed) => {
                    setForm((p) => ({
                      ...p,
                      venue: parsed.name || parsed.street || p.venue,
                      address: parsed.street || p.address,
                      city: parsed.city || p.city,
                      state: parsed.state || p.state,
                      zip_code: parsed.zip || p.zip_code,
                    }));
                    flashAutofilled(['er-address', 'er-city', 'er-state', 'er-zip']);
                  }}
                  placeholder="Community Center"
                  types={['establishment']}
                  testId="er-venue"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Address</Label>
                <AddressAutocomplete
                  value={form.address}
                  onChange={(v) => set('address', v)}
                  onPlaceSelected={(parsed) => {
                    setForm((p) => ({
                      ...p,
                      address: parsed.street || parsed.formatted,
                      city: parsed.city || p.city,
                      state: parsed.state || p.state,
                      zip_code: parsed.zip || p.zip_code,
                    }));
                    flashAutofilled(['er-city', 'er-state', 'er-zip']);
                  }}
                  placeholder="100 Main Street"
                  testId="er-address"
                />
              </div>
              <div>
                <Label>City</Label>
                <Input
                  value={form.city}
                  onChange={(e) => set('city', e.target.value)}
                  data-testid="er-city"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>State</Label>
                  <Input
                    value={form.state}
                    onChange={(e) => set('state', e.target.value)}
                    maxLength={2}
                    data-testid="er-state"
                  />
                </div>
                <div>
                  <Label>ZIP</Label>
                  <Input
                    value={form.zip_code}
                    onChange={(e) => set('zip_code', e.target.value)}
                    data-testid="er-zip"
                  />
                </div>
              </div>
              <div>
                <Label>Contact person</Label>
                <Input
                  value={form.contact_name}
                  onChange={(e) => set('contact_name', e.target.value)}
                  data-testid="er-contact-name"
                />
              </div>
              <div>
                <Label>Contact phone</Label>
                <Input
                  value={formatPhoneInput(form.contact_phone)}
                  onChange={(e) => set('contact_phone', e.target.value)}
                  data-testid="er-contact-phone"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Contact email</Label>
                <Input
                  type="email"
                  value={form.contact_email}
                  onChange={(e) => set('contact_email', e.target.value)}
                  data-testid="er-contact-email"
                />
              </div>
            </div>
          </section>

          {/* ── Business Opportunity ─────────────────────────── */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-800">Business Opportunity</h3>
                <p className="text-xs text-slate-500">
                  Projected <strong>number of sales</strong> per category. Optional but recommended.
                </p>
              </div>
              <Button
                type="button" size="sm" variant="outline"
                onClick={addSalesLine}
                data-testid="er-add-sales-line"
              >
                <Plus size={12} className="mr-1" /> Add category
              </Button>
            </div>
            {form.projected_sales.length === 0 ? (
              <p className="text-[11px] text-slate-400 italic border border-dashed rounded p-3 text-center">
                No projections yet — click &ldquo;Add category&rdquo; to estimate potential sales.
              </p>
            ) : (
              <div className="space-y-2">
                {form.projected_sales.map((line, idx) => (
                  <div
                    key={idx}
                    className="grid grid-cols-12 gap-2 items-center"
                    data-testid={`er-sales-line-${idx}`}
                  >
                    <div className="col-span-6">
                      <Select
                        value={line.category}
                        onValueChange={(v) => updateSalesLine(idx, { category: v })}
                      >
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {(enums.business_categories || []).map((c) => (
                            <SelectItem key={c} value={c}>{CATEGORY_LABELS[c] || c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-5 relative">
                      <Hash size={13} className="absolute left-2 top-2.5 text-slate-400" />
                      <Input
                        type="number" min={0} step="1"
                        className="h-9 pl-7 text-right"
                        value={line.amount}
                        onChange={(e) => updateSalesLine(idx, { amount: e.target.value })}
                        placeholder="# of sales"
                        data-testid={`er-sales-amount-${idx}`}
                      />
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <Button
                        type="button" variant="ghost" size="icon"
                        className="h-8 w-8 text-rose-500"
                        onClick={() => removeSalesLine(idx)}
                        data-testid={`er-sales-remove-${idx}`}
                      >
                        <Trash2 size={13} />
                      </Button>
                    </div>
                  </div>
                ))}
                <div className="flex justify-end pt-2 border-t border-slate-100">
                  <div className="text-sm">
                    <span className="text-slate-500">Total projected sales:</span>{' '}
                    <span className="font-semibold text-slate-900" data-testid="er-total-projected">
                      {totalProjected.toLocaleString()} sale{totalProjected === 1 ? '' : 's'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* ── Cost Information ────────────────────────────── */}
          <section>
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Cost Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Total Event Cost *</Label>
                <div className="relative">
                  <DollarSign size={14} className="absolute left-3 top-2.5 text-slate-400" />
                  <Input
                    type="number" min={0} step="1" className="pl-8"
                    value={form.total_event_cost}
                    onChange={(e) => set('total_event_cost', e.target.value)}
                    placeholder="e.g. 1200"
                    data-testid="er-total-cost"
                  />
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  How much the event actually costs to run.
                </p>
              </div>
              <div>
                <Label>Amount Requested from KR *</Label>
                <div className="relative">
                  <DollarSign size={14} className="absolute left-3 top-2.5 text-slate-400" />
                  <Input
                    type="number" min={0} step="1" className="pl-8"
                    value={form.amount_requested}
                    onChange={(e) => set('amount_requested', e.target.value)}
                    placeholder="e.g. 800"
                    data-testid="er-amount-requested"
                  />
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  How much you&apos;re asking KR to cover — can be less than total cost.
                </p>
              </div>
            </div>
          </section>
        </div>

        <DialogFooter className="mt-4">
          <Button
            type="button" variant="outline"
            onClick={() => onOpenChange(false)} disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={saving || !canSubmit}
            data-testid="er-submit-btn"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isEdit ? 'Save Changes' : 'Submit Request'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
